/* Vendored from the board kit on feat/board-kit-legend-scroll (b834d95, verifier-CONFIRMED, not yet on master).
 * Manifest Review needs the docked record and the top scroll rail now; when that branch lands, import this
 * from components/board instead and delete this copy. Kept byte-close to the kit on purpose. */
/**
 * The open record, DOCKED beside the table (owner, 2026-10-05: "when opening
 * up boards in the table I do not want some disgustingly long dropdown that
 * is super difficult to navigate"). It replaces the panel that used to unfold
 * inside the table under its row.
 *
 *   - A labelled region to the right of the table (about 44% of the board,
 *     capped), so the table stays visible and scrollable with both scroll
 *     bars, and the open row stays highlighted.
 *   - Its own scroll, with a sticky header: the record's identity, where it
 *     sits on this page, Previous / Next (also Alt+Up / Alt+Down), and Close.
 *   - Long content comes as SECTIONS: a sticky list of anchors at the top
 *     and one collapsible block per section, remembered per board.
 *     A single section (the `renderDetail` boards) is drawn without chrome.
 *   - Below 900px it is a full-height sheet over the board with a Back
 *     button; the table behind it is inert until the sheet closes.
 *
 * Opening, closing, Escape, focus and the URL are BoardTable's; this file
 * only draws what it is given.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { booleanRecord, readMemory, writeMemory } from './memory';

export interface BoardDetailSection {
  /** Stable id, used for the anchor and the remembered collapsed state. */
  id: string;
  /** The section's visible name in the anchor list and its header. */
  label: string;
  render: () => React.ReactNode;
  /** Start collapsed until the viewer opens it (then remembered). */
  defaultCollapsed?: boolean;
}

/** True below `maxWidth` (inclusive). False where matchMedia does not exist. */
export function useNarrow(maxWidth = 899): boolean {
  const query = `(max-width: ${maxWidth}px)`;
  const read = () => (typeof window !== 'undefined' && typeof window.matchMedia === 'function' ? window.matchMedia(query).matches : false);
  const [narrow, setNarrow] = useState(read);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia(query);
    const on = () => setNarrow(mq.matches);
    on();
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', on);
      return () => mq.removeEventListener('change', on);
    }
    mq.addListener(on);
    return () => mq.removeListener(on);
  }, [query]);
  return narrow;
}

function prefersReducedMotion(): boolean {
  try { return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch { return false; }
}

export interface DetailDockProps {
  id: string;
  /** The region's accessible name ("NORTH RIDGE: operations and Monday reference"). */
  label: string;
  /** The record's identity as the row shows it. */
  title: React.ReactNode;
  /** 1-based place among the openable rows of this page, and how many there are. */
  index: number;
  count: number;
  onPrev: (() => void) | null;
  onNext: (() => void) | null;
  onClose: () => void;
  sheet: boolean;
  sections: BoardDetailSection[];
  memoryKey: string;
  headingRef: React.RefObject<HTMLHeadingElement>;
}

export default function DetailDock({
  id, label, title, index, count, onPrev, onNext, onClose, sheet, sections, memoryKey, headingRef,
}: DetailDockProps) {
  const store = `sections.${memoryKey}`;
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>(() => readMemory<Record<string, boolean>>(store, {}, booleanRecord));
  const [active, setActive] = useState<string | null>(null);
  const isCollapsed = (s: BoardDetailSection) => (Object.prototype.hasOwnProperty.call(collapsed, s.id) ? collapsed[s.id] : Boolean(s.defaultCollapsed));
  const setOne = useCallback((sid: string, value: boolean) => {
    setCollapsed((prev) => {
      const next = { ...prev, [sid]: value };
      writeMemory(store, next);
      return next;
    });
  }, [store]);
  const sectionId = (sid: string) => `${id}-sec-${sid}`;
  const toggleId = (sid: string) => `${id}-sec-${sid}-toggle`;

  // A new record starts with no anchor marked.
  useEffect(() => { setActive(null); }, [id, label]);

  const jump = (e: React.MouseEvent, s: BoardDetailSection) => {
    e.preventDefault();
    if (isCollapsed(s)) setOne(s.id, false);
    setActive(s.id);
    const el = document.getElementById(sectionId(s.id));
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'start', behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    const btn = document.getElementById(toggleId(s.id)) as HTMLButtonElement | null;
    if (btn) btn.focus({ preventScroll: true });
  };

  const multi = sections.length > 1;
  return (
    <section
      id={id}
      className={`board-dock${sheet ? ' board-dock--sheet' : ''}`}
      aria-label={label}
      data-mode={sheet ? 'sheet' : 'docked'}
    >
      <header className="board-dock__head">
        {sheet && (
          <button type="button" className="board-dock__back" onClick={onClose}>
            <span aria-hidden="true">&larr;</span> Back to the board
          </button>
        )}
        <div className="board-dock__id">
          <p className="board-dock__where" aria-live="polite">Open record &middot; {index} of {count} on this page</p>
          <h2 className="board-dock__title" ref={headingRef} tabIndex={-1}>{title}</h2>
        </div>
        <div className="board-dock__nav-btns">
          <button type="button" className="board-dock__btn" onClick={onPrev || undefined} disabled={!onPrev} aria-keyshortcuts="Alt+ArrowUp" title="Previous record (Alt+Up)">
            <span aria-hidden="true">&uarr;</span> Previous<span className="board-sr"> record</span>
          </button>
          <button type="button" className="board-dock__btn" onClick={onNext || undefined} disabled={!onNext} aria-keyshortcuts="Alt+ArrowDown" title="Next record (Alt+Down)">
            <span aria-hidden="true">&darr;</span> Next<span className="board-sr"> record</span>
          </button>
          {!sheet && (
            <button type="button" className="board-dock__btn board-dock__close" onClick={onClose} aria-keyshortcuts="Escape" title="Close (Esc)">
              <span aria-hidden="true">&times;</span> Close<span className="board-sr"> the open record</span>
            </button>
          )}
        </div>
      </header>
      <div className="board-dock__body board-detail" data-sections={sections.length}>
        {multi && (
          <nav className="board-dock__anchors" aria-label="Sections of the open record">
            <ul>
              {sections.map((s) => (
                <li key={s.id}>
                  <a href={`#${sectionId(s.id)}`} onClick={(e) => jump(e, s)} aria-current={active === s.id ? 'true' : undefined}>
                    {s.label}
                    {isCollapsed(s) && <span className="board-sr"> (collapsed)</span>}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        )}
        {multi ? sections.map((s) => {
          const shut = isCollapsed(s);
          const bodyId = `${id}-sec-${s.id}-body`;
          return (
            <section key={s.id} id={sectionId(s.id)} className="board-dock__section" aria-labelledby={toggleId(s.id)} data-collapsed={shut ? 'true' : 'false'}>
              <h3 className="board-dock__shead">
                <button type="button" id={toggleId(s.id)} aria-expanded={!shut} aria-controls={bodyId} onClick={() => setOne(s.id, !shut)}>
                  <span className="board-caret" aria-hidden="true" />
                  {s.label}
                </button>
              </h3>
              {/* Collapsed content stays mounted (hidden), so a form or a fetch inside it is not thrown away. */}
              <div id={bodyId} className="board-dock__sbody" hidden={shut}>{s.render()}</div>
            </section>
          );
        }) : sections.map((s) => <div key={s.id} className="board-dock__single" data-section={s.id}>{s.render()}</div>)}
      </div>
    </section>
  );
}
